// src/pages/ContentModerationPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Filter, 
  Shield, 
  AlertTriangle,
  LayoutGrid,
  List
} from 'lucide-react';
import ReportCard from '../components/reports/ReportCard';
import { reportsService } from '../services/reportsService';

import ModeratedContentList from '../components/reports/ModeratedContentList';
import ModerationStats from '../components/reports/ModerationStats';
import ReportReviewModal from '../components/reports/ReportReviewModal';
import ReportHistoryModal from '../components/reports/ReportHistoryModal';


const ContentModerationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    inReview: 0,
    highPriority: 0,
    resolvedToday: 0
  });
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [reportHistory, setReportHistory] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  console.log('[ContentModerationPage - Mount]', { viewMode });

  const fetchData = useCallback(async () => {
    console.log('[ContentModerationPage - Fetching Data]');
    try {
      setLoading(true);
      const [reportsData, statsData] = await Promise.all([
        // Use the unified method with content type
        reportsService.getFlaggedContentByType({
          contentType: 'all',
          page: currentPage,
          limit: viewMode === 'grid' ? 9 : 20,
          status: searchParams.get('status') || '',
          severity: searchParams.get('severity') || '',
          sortBy: searchParams.get('sort') || 'createdAt'
        }),
        // Use unified stats method
        reportsService.getStatsByContentType('all', 24)
      ]);
  
      setReports(reportsData.reports);
      setTotalPages(reportsData.totalPages);
      setStats({
        pending: statsData.pending || 0,
        inReview: statsData.inReview || 0,
        highPriority: reportsData.reports.filter(r => r.reportCount > 2).length,
        resolvedToday: statsData.resolved || 0
      });
    } catch (err) {
      console.error('[ContentModerationPage - Fetch Error]', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchParams, viewMode]);
  
  useEffect(() => {
    fetchData();
  }, [currentPage, searchParams, fetchData]);

  const handleBatchAction = async (action) => {
    console.log('[ContentModerationPage - Batch Action]', {
      action,
      reports: selectedReports
    });

    try {
      await Promise.all(
        selectedReports.map(reportId =>
          reportsService.reviewReport(reportId, {
            action,
            notes: `Batch ${action}`
          })
        )
      );
      setSelectedReports([]);
      fetchData();
    } catch (err) {
      console.error('[ContentModerationPage - Batch Action Error]', err);
      setError(err.message);
    }
  };

  const handleReportAction = async (reportId, action) => {
    console.log('[ContentModerationPage - Report Action]', { reportId, action });
    
    try {
      if (action === 'view_history') {
        handleViewHistory(reportId);
        return;
      }
      
      await reportsService.reviewReport(reportId, {
        action,
        notes: `Action taken: ${action}`
      });
      
      fetchData();
    } catch (err) {
      console.error('[ContentModerationPage - Action Error]', err);
      setError(err.message);
    }
  };

  const handleViewHistory = async (reportId) => {
    try {
      const history = await reportsService.getReportHistory(reportId);
      setReportHistory(history);
      setIsHistoryModalOpen(true);
    } catch (err) {
      console.error('[ContentModerationPage - View History Error]', err);
      setError(err.message);
    }
  };

  const toggleViewMode = () => {
    console.log('[ContentModerationPage - Toggling View Mode]', {
      current: viewMode,
      new: viewMode === 'grid' ? 'list' : 'grid'
    });
    setViewMode(viewMode === 'grid' ? 'list' : 'grid');
    setCurrentPage(1); // Reset to first page when switching views
  };

  if (error) {
    return (
      <div className="p-4 text-red-600 bg-red-50 rounded-lg">
        Error loading content: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Content Moderation</h1>
          <p className="mt-1 text-sm text-gray-500">Review and moderate reported content</p>
        </div>
        
        <div className="flex space-x-3">
          {selectedReports.length > 0 ? (
            <>
              <button
                onClick={() => handleBatchAction('resolved')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Shield className="h-5 w-5" />
                <span>Resolve Selected ({selectedReports.length})</span>
              </button>
              <button
                onClick={() => handleBatchAction('content_removed')}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2"
              >
                <AlertTriangle className="h-5 w-5" />
                <span>Remove Content ({selectedReports.length})</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={toggleViewMode}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
              >
                {viewMode === 'grid' ? (
                  <>
                    <List className="h-5 w-5" />
                    <span>List View</span>
                  </>
                ) : (
                  <>
                    <LayoutGrid className="h-5 w-5" />
                    <span>Grid View</span>
                  </>
                )}
              </button>
              <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2">
                <Filter className="h-5 w-5" />
                <span>Filters</span>
              </button>
              <select 
                className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
                onChange={(e) => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('sort', e.target.value);
                  setSearchParams(newParams);
                }}
              >
                <option value="createdAt">Newest First</option>
                <option value="reportCount">Most Reported</option>
                <option value="updatedAt">Recently Updated</option>
              </select>
            </>
          )}
        </div>
      </div>

      {/* Stats Overview */}
      <ModerationStats stats={stats} contentType="all" />

      {/* Content View */}
      {viewMode === 'list' ? (
        <ModeratedContentList
          items={reports}
          loading={loading}
          contentType="report"
          selectedItems={selectedReports}
          onSelectionChange={setSelectedReports}
          onReviewClick={setSelectedReportId}
          onAction={handleReportAction}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((report) => (
            <ReportCard
              key={report._id}
              report={report}
              onReview={() => setSelectedReportId(report._id)}
              onRemove={(id) => handleReportAction(id, 'content_removed')}
              onAction={handleReportAction}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </p>
        <div className="flex space-x-2">
          <button
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 disabled:opacity-50"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <button
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 disabled:opacity-50"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {/* Modals */}
      <ReportReviewModal
        reportId={selectedReportId}
        isOpen={!!selectedReportId}
        onClose={() => setSelectedReportId(null)}
        onAction={handleReportAction}
        onReviewComplete={() => {
          fetchData();
          setSelectedReportId(null);
        }}
        contentType="report"
      />

      {isHistoryModalOpen && (
        <ReportHistoryModal
          isOpen={isHistoryModalOpen}
          onClose={() => {
            setIsHistoryModalOpen(false);
            setReportHistory(null);
          }}
          history={reportHistory}
        />
      )}
    </div>
  );
};

export default ContentModerationPage;